// src/pages/CreditApplicationDetails.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  CreditCard,
  ArrowLeft,
  FileText,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  Clock,
  Download,
  Copy,
  Send,
  RotateCcw,
  Ban,
  UserCheck,
  MapPin,
  Calendar,
  Building2,
  Phone,
  Mail,
  AlertTriangle,
  History,
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { CreditApplicationDetail } from '../types/creditApplications';
import { CreditDocumentPreview } from '../components/credit/CreditDocumentPreview';

export const CreditApplicationDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, can } = useAuth();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [app, setApp] = useState<CreditApplicationDetail | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'preview' | 'verification' | 'assessment' | 'history'>('details');

  // Internal Assessment Form State
  const [riskConcerns, setRiskConcerns] = useState('');
  const [paymentHistory, setPaymentHistory] = useState('');
  const [recommendation, setRecommendation] = useState('');
  const [otherComments, setOtherComments] = useState('');
  const [salespersonSignature, setSalespersonSignature] = useState('');
  const [authorizedDate, setAuthorizedDate] = useState('');
  const [savingAssessment, setSavingAssessment] = useState(false);

  // Approval/Rejection Modals
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processingAction, setProcessingAction] = useState(false);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const res = await fetch(`/api/credit-applications/${id}`);
      const data = await res.json<any>();
      if (!res.ok || !data.success) {
        throw new Error(data.error?.message || 'Failed to fetch application');
      }

      const item = data.data;
      setApp(item);

      if (item.review) {
        setRiskConcerns(item.review.credit_risk_concerns || '');
        setPaymentHistory(item.review.payment_history || '');
        setRecommendation(item.review.salesperson_recommendation || '');
        setOtherComments(item.review.other_comments || '');
        setSalespersonSignature(item.review.salesperson_signature || '');
        setAuthorizedDate(item.review.authorized_date || '');
      } else {
        setAuthorizedDate(new Date().toISOString().split('T')[0]);
      }
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchDetail();
  }, [id]);

  const handleSaveAssessment = async () => {
    setSavingAssessment(true);
    try {
      const res = await fetch(`/api/credit-applications/${id}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          credit_risk_concerns: riskConcerns,
          payment_history: paymentHistory,
          salesperson_recommendation: recommendation,
          other_comments: otherComments,
          salesperson_signature: salespersonSignature,
          authorized_date: authorizedDate,
        }),
      });
      const data = await res.json<any>();
      if (!res.ok || !data.success) {
        throw new Error(data.error?.message || 'Failed to save assessment');
      }
      showToast('Internal assessment updated.', 'success');
      fetchDetail();
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setSavingAssessment(false);
    }
  };

  const handleApprove = async () => {
    setProcessingAction(true);
    try {
      const res = await fetch(`/api/credit-applications/${id}/approve`, {
        method: 'POST',
      });
      const data = await res.json<any>();
      if (!res.ok || !data.success) {
        throw new Error(data.error?.message || 'Failed to approve application');
      }
      showToast('Credit application APPROVED.', 'success');
      setShowApproveConfirm(false);
      fetchDetail();
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleReject = async () => {
    setProcessingAction(true);
    try {
      const res = await fetch(`/api/credit-applications/${id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: rejectionReason }),
      });
      const data = await res.json<any>();
      if (!res.ok || !data.success) {
        throw new Error(data.error?.message || 'Failed to reject application');
      }
      showToast('Credit application marked REJECTED.', 'success');
      setShowRejectModal(false);
      fetchDetail();
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setProcessingAction(false);
    }
  };

  const downloadDocument = (type: 'application_form' | 'verification_report') => {
    window.open(`/api/credit-applications/${id}/documents/download?type=${type}`, '_blank');
  };

  if (loading || !app) {
    return (
      <div className="py-16 text-center">
        <div className="w-8 h-8 border-4 border-[#0f5a2e] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm text-slate-500">Loading credit application details...</p>
      </div>
    );
  }

  const snap = app.submission ? JSON.parse(app.submission.snapshot_json) : null;

  return (
    <div className="space-y-6 pb-16">
      {/* Top Banner Navigation */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to="/credit-applications">
            <Button variant="outline" size="sm" className="p-2">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-slate-900">{app.company_name}</h1>
              <Badge status={app.status} />
            </div>
            <p className="text-xs text-slate-500 font-mono mt-0.5">Ref: {app.reference_no}</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {app.status === 'submitted' || app.status === 'under_review' ? (
            <>
              {can('credit_applications.approve') && (
                <Button
                  onClick={() => setShowApproveConfirm(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs flex items-center gap-1.5"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Approve Credit</span>
                </Button>
              )}
              {can('credit_applications.reject') && (
                <Button
                  variant="outline"
                  onClick={() => setShowRejectModal(true)}
                  className="text-red-600 border-red-200 hover:bg-red-50 text-xs flex items-center gap-1.5"
                >
                  <XCircle className="w-4 h-4" />
                  <span>Reject</span>
                </Button>
              )}
            </>
          ) : null}

          {can('credit_applications.download') && (
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadDocument('application_form')}
                className="text-xs flex items-center gap-1.5"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Application PDF</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadDocument('verification_report')}
                className="text-xs flex items-center gap-1.5"
              >
                <ShieldCheck className="w-3.5 h-3.5 text-[#0f5a2e]" />
                <span>Verification PDF</span>
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Tabs Bar */}
      <div className="border-b border-slate-200 flex gap-4 text-xs font-semibold">
        <button
          onClick={() => setActiveTab('details')}
          className={`pb-2.5 transition-colors border-b-2 ${
            activeTab === 'details'
              ? 'border-[#0f5a2e] text-[#0f5a2e]'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          Overview & Request
        </button>
        <button
          onClick={() => setActiveTab('preview')}
          className={`pb-2.5 transition-colors border-b-2 ${
            activeTab === 'preview'
              ? 'border-[#0f5a2e] text-[#0f5a2e]'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          Customer Form (CIF)
        </button>
        <button
          onClick={() => setActiveTab('verification')}
          className={`pb-2.5 transition-colors border-b-2 ${
            activeTab === 'verification'
              ? 'border-[#0f5a2e] text-[#0f5a2e]'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          Verification Evidence
        </button>
        <button
          onClick={() => setActiveTab('assessment')}
          className={`pb-2.5 transition-colors border-b-2 ${
            activeTab === 'assessment'
              ? 'border-[#0f5a2e] text-[#0f5a2e]'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          Internal Assessment
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`pb-2.5 transition-colors border-b-2 ${
            activeTab === 'history'
              ? 'border-[#0f5a2e] text-[#0f5a2e]'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          Email & Audit Logs
        </button>
      </div>

      {/* TAB 1: DETAILS */}
      {activeTab === 'details' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-6">
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
              <h3 className="text-sm font-bold text-slate-900">Requested Commercial Facility</h3>
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <span className="text-slate-500 block">Requested Credit Limit</span>
                  <span className="text-lg font-bold text-[#0f5a2e] mt-0.5 block">
                    {app.answers?.requested_credit_limit
                      ? `${app.currency} ${Number(app.answers.requested_credit_limit).toLocaleString('en-IN')}`
                      : 'Pending entry'}
                  </span>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <span className="text-slate-500 block">Requested Credit Terms</span>
                  <span className="text-base font-bold text-slate-900 mt-0.5 block">
                    {app.answers?.requested_credit_terms || 'Pending entry'}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 block">Credit Type</span>
                  <span className="font-semibold text-slate-800">{app.answers?.credit_type || '—'}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">Expected Purchase Volume</span>
                  <span className="font-semibold text-slate-800">{app.answers?.expected_purchase_volume || '—'}</span>
                </div>
              </div>

              <div>
                <span className="text-slate-500 text-xs block mb-1">Reason for Credit Request (Business Justification):</span>
                <p className="text-xs bg-slate-50 p-3 rounded-lg border border-slate-200 leading-relaxed text-slate-800">
                  {app.answers?.business_justification || 'Not submitted yet'}
                </p>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-3">
              <h3 className="text-sm font-bold text-slate-900">Applicant Company Background</h3>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <span className="text-slate-500 block">Business Type / Industry</span>
                  <span className="font-medium text-slate-900">{app.answers?.business_type || '—'}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">Established Since</span>
                  <span className="font-medium text-slate-900">{app.answers?.established_since || '—'}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">Relationship Duration with Geobit</span>
                  <span className="font-medium text-slate-900">{app.answers?.relationship_duration || 'New Customer'}</span>
                </div>
              </div>
              <div>
                <span className="text-slate-500 text-xs block mb-1">Brief Description of Customer & Market Position:</span>
                <p className="text-xs bg-slate-50 p-3 rounded-lg border border-slate-200 leading-relaxed text-slate-800">
                  {app.answers?.description_and_market_position || 'Not provided'}
                </p>
              </div>
            </div>
          </div>

          {/* Right Column: Signatory & Meta */}
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-3 text-xs">
              <h3 className="text-sm font-bold text-slate-900">Authorized Signatory</h3>
              {app.submission ? (
                <div className="space-y-2">
                  <div>
                    <span className="text-slate-500 block">Signatory Name</span>
                    <span className="font-bold text-slate-900">{app.submission.signatory_name}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Designation</span>
                    <span className="font-medium text-slate-800">{app.submission.signatory_designation}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Signatory Email</span>
                    <span className="font-mono text-slate-800">{app.submission.signatory_email}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Signatory Phone</span>
                    <span className="font-mono text-slate-800">{app.submission.signatory_phone}</span>
                  </div>
                  <div className="pt-2">
                    <span className="text-slate-500 block mb-1">Electronic Signature</span>
                    {app.submission.signature_method === 'draw' ? (
                      <img
                        src={app.submission.signature_data}
                        alt="Signature"
                        className="h-12 border border-slate-200 p-1 bg-white rounded"
                      />
                    ) : (
                      <div className="font-serif italic text-base font-bold text-[#0f5a2e]">
                        {app.submission.signatory_name}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-slate-400 italic">No submission signed yet.</p>
              )}
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-3 text-xs">
              <h3 className="text-sm font-bold text-slate-900">Application Metadata</h3>
              <div>
                <span className="text-slate-500 block">Salesperson</span>
                <span className="font-medium text-slate-900">{app.salesperson_name} ({app.sales_region})</span>
              </div>
              <div>
                <span className="text-slate-500 block">Created At</span>
                <span className="font-mono text-slate-700">{app.created_at}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Expires At</span>
                <span className="font-mono text-slate-700">{app.expires_at}</span>
              </div>
              {app.submitted_at && (
                <div>
                  <span className="text-slate-500 block">Submitted At</span>
                  <span className="font-mono text-emerald-700 font-bold">{app.submitted_at}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: PREVIEW */}
      {activeTab === 'preview' && (
        <div className="space-y-4">
          <CreditDocumentPreview
            app={app}
            answers={{
              business_type: app.answers?.business_type || '',
              established_since: app.answers?.established_since || '',
              customer_emails: app.answers?.customer_emails || [],
              customer_phones: app.answers?.customer_phones || [],
              description_and_market_position: app.answers?.description_and_market_position || '',
              relationship_duration: app.answers?.relationship_duration || 'New Customer',
              additional_customer_info: '',
              requested_credit_limit: app.answers?.requested_credit_limit ?? '',
              requested_credit_terms: app.answers?.requested_credit_terms || '',
              custom_credit_terms: app.answers?.custom_credit_terms || '',
              credit_type: app.answers?.credit_type || '',
              credit_type_other: app.answers?.credit_type_other || '',
              expected_purchase_volume: app.answers?.expected_purchase_volume || '',
              business_justification: app.answers?.business_justification || '',
            }}
            signatory={
              app.submission
                ? {
                    name: app.submission.signatory_name,
                    designation: app.submission.signatory_designation,
                    company: app.submission.signatory_company,
                    email: app.submission.signatory_email,
                    phone: app.submission.signatory_phone,
                    signature_data: app.submission.signature_data,
                    signature_method: app.submission.signature_method,
                  }
                : undefined
            }
          />
        </div>
      )}

      {/* TAB 3: VERIFICATION EVIDENCE */}
      {activeTab === 'verification' && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-6 text-xs">
          <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
            <ShieldCheck className="w-5 h-5 text-[#0f5a2e]" />
            <div>
              <h3 className="text-sm font-bold text-slate-900">Digital Submission Footprint & Verification</h3>
              <p className="text-slate-500">Immutable edge telemetry captured upon customer submission</p>
            </div>
          </div>

          {app.verification ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                <span className="font-bold text-slate-800 uppercase tracking-wider block">Network & Geolocation</span>
                <div>
                  <span className="text-slate-500 block">Client IP Address:</span>
                  <span className="font-mono font-bold text-slate-900">{app.verification.ip_address}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">IP Derived Location:</span>
                  <span className="font-semibold text-slate-900">
                    {[app.verification.ip_city, app.verification.ip_region, app.verification.ip_country].filter(Boolean).join(', ')}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 block">GPS Permission:</span>
                  <span className="font-medium text-slate-800 uppercase">{app.verification.gps_permission_outcome}</span>
                </div>
                {app.verification.gps_latitude && (
                  <div>
                    <span className="text-slate-500 block">GPS Coordinates:</span>
                    <span className="font-mono text-slate-900">
                      {app.verification.gps_latitude}, {app.verification.gps_longitude} (±{app.verification.gps_accuracy_meters}m)
                    </span>
                  </div>
                )}
              </div>

              <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                <span className="font-bold text-slate-800 uppercase tracking-wider block">Device & Identity Verification</span>
                <div>
                  <span className="text-slate-500 block">Signatory Email OTP:</span>
                  <span className="font-bold text-emerald-700">
                    {app.verification.email_verified ? 'VERIFIED (Resend OTP Match)' : 'UNVERIFIED'}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 block">Browser / OS / Device:</span>
                  <span className="text-slate-900 font-medium">
                    {app.verification.browser} / {app.verification.os} ({app.verification.device_category})
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 block">Application PDF SHA-256:</span>
                  <span className="font-mono text-[11px] text-slate-700 break-all">
                    {app.verification.application_pdf_hash || 'Archived'}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 block">Audit Event Reference:</span>
                  <span className="font-mono font-bold text-[#0f5a2e]">{app.verification.audit_event_reference}</span>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-slate-400 italic">Submission evidence will be captured once the customer submits the application.</p>
          )}
        </div>
      )}

      {/* TAB 4: INTERNAL ASSESSMENT */}
      {activeTab === 'assessment' && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-6 text-xs">
          <div>
            <h3 className="text-sm font-bold text-slate-900">Internal Risk & Credit Assessment</h3>
            <p className="text-slate-500 mt-0.5">
              Original fields from Document Reference GIND/CIF/01/2026 reserved for internal commercial sign-off.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block font-bold text-slate-800 mb-1">
                Any Credit Risk Concerns / Mitigating Factors (Section 3.3)
              </label>
              <textarea
                rows={3}
                value={riskConcerns}
                onChange={e => setRiskConcerns(e.target.value)}
                placeholder="Identify any commercial risks (e.g. exposure to specific projects, payment history with competitors)..."
                className="w-full p-2.5 border border-slate-300 rounded-md focus:ring-1 focus:ring-[#0f5a2e]"
              />
            </div>

            <div>
              <label className="block font-bold text-slate-800 mb-1">
                Customer's Payment History (if applicable) (Section 5.2)
              </label>
              <textarea
                rows={3}
                value={paymentHistory}
                onChange={e => setPaymentHistory(e.target.value)}
                placeholder="Details of previous invoice payment behavior, PDC clearance track record, or ledger statement..."
                className="w-full p-2.5 border border-slate-300 rounded-md focus:ring-1 focus:ring-[#0f5a2e]"
              />
            </div>

            <div>
              <label className="block font-bold text-slate-800 mb-1">
                Salesperson's Recommendation (Section 5.3)
              </label>
              <textarea
                rows={3}
                value={recommendation}
                onChange={e => setRecommendation(e.target.value)}
                placeholder="Commercial justification by sales representative recommending limit approval..."
                className="w-full p-2.5 border border-slate-300 rounded-md focus:ring-1 focus:ring-[#0f5a2e]"
              />
            </div>

            <div>
              <label className="block font-bold text-slate-800 mb-1">
                Any Other Comments (Section 5.4)
              </label>
              <textarea
                rows={2}
                value={otherComments}
                onChange={e => setOtherComments(e.target.value)}
                placeholder="Management caveats, security cheque requirements, etc."
                className="w-full p-2.5 border border-slate-300 rounded-md focus:ring-1 focus:ring-[#0f5a2e]"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-slate-200">
              <div>
                <label className="block font-bold text-slate-800 mb-1">Salesperson Signature (Section 6)</label>
                <input
                  type="text"
                  value={salespersonSignature}
                  onChange={e => setSalespersonSignature(e.target.value)}
                  placeholder="Sales Rep Name / Digital Attestation"
                  className="w-full p-2 border border-slate-300 rounded-md focus:ring-1 focus:ring-[#0f5a2e]"
                />
              </div>
              <div>
                <label className="block font-bold text-slate-800 mb-1">Authorization Date (Section 6)</label>
                <input
                  type="date"
                  value={authorizedDate}
                  onChange={e => setAuthorizedDate(e.target.value)}
                  className="w-full p-2 border border-slate-300 rounded-md focus:ring-1 focus:ring-[#0f5a2e]"
                />
              </div>
            </div>

            <div className="pt-3 flex justify-end">
              <Button
                type="button"
                onClick={handleSaveAssessment}
                disabled={savingAssessment}
                className="bg-[#0f5a2e] hover:bg-[#0c4724] text-white"
              >
                {savingAssessment ? 'Saving...' : 'Save Internal Assessment'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* TAB 5: HISTORY */}
      {activeTab === 'history' && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4 text-xs">
          <h3 className="text-sm font-bold text-slate-900">Notification & Activity History</h3>
          <div className="divide-y divide-slate-100">
            {app.email_logs && app.email_logs.length > 0 ? (
              app.email_logs.map(log => (
                <div key={log.id} className="py-2.5 flex items-center justify-between">
                  <div>
                    <span className="font-semibold text-slate-800">{log.subject}</span>
                    <div className="text-slate-500 text-[11px]">
                      To: {log.recipient_email} • Status: <span className="font-bold uppercase text-emerald-700">{log.status}</span>
                    </div>
                  </div>
                  <span className="font-mono text-slate-400 text-[11px]">{log.sent_at}</span>
                </div>
              ))
            ) : (
              <p className="text-slate-400 py-4 italic">No dispatch logs found.</p>
            )}
          </div>
        </div>
      )}

      {/* Approve Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showApproveConfirm}
        onClose={() => setShowApproveConfirm(false)}
        onConfirm={handleApprove}
        title="Approve Credit Application"
        message={`Are you sure you want to approve the credit facility request for ${app.company_name}? This will record official executive approval.`}
        confirmLabel="Approve Facility"
        variant="primary"
      />

      {/* Reject Modal */}
      <Modal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Reject Credit Application"
      >
        <div className="space-y-4 text-xs">
          <p className="text-slate-600">
            Please enter a justification for declining the credit request for <strong>{app.company_name}</strong>:
          </p>
          <textarea
            rows={3}
            value={rejectionReason}
            onChange={e => setRejectionReason(e.target.value)}
            placeholder="e.g. Inadequate payment history or unmitigated commercial risk..."
            className="w-full p-2 border border-slate-300 rounded-md focus:ring-1 focus:ring-red-500"
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowRejectModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              disabled={processingAction}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {processingAction ? 'Rejecting...' : 'Confirm Rejection'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
